// ScoreAnimations.jsx
// /var/www/binko-frontend/src/components/ScoreAnimations.jsx
//
// Reward animation primitives for Binko.
// CSS keyframes injected once on first import — zero npm dependencies.
//
// Exports:
//   ScorePops          — floating +10 / +5 Speed / +5 Streak pops
//   ScoreCounter       — number that scale-pulses when value changes
//   StreakBar          — 5-dot streak indicator, glows orange at 3+
//   ResultScoreCard    — animated score breakdown for RESULT phase
//   MatchOverlay       — full-screen "It\'s a match" overlay
//   XPBar              — rank badge + animated progress bar (Profile)
//   RankUpToast        — yellow pill toast when rank crosses threshold
//   getRank            — returns { name, next, nextMin, pct } for any score
//   RANK_CONFIG        — bracket definitions

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { C } from '../theme';

// ── Keyframe injection (once) ─────────────────────────────────────────────────
let _injected = false;
function injectStyles() {
  if (_injected || typeof document === 'undefined') return;
  _injected = true;
  const s = document.createElement('style');
  s.textContent = `
    @keyframes bk-float-up {
      0%   { opacity:0;   transform:translateY(0px)   scale(0.7); }
      15%  { opacity:1;   transform:translateY(-8px)  scale(1.06); }
      70%  { opacity:1;   transform:translateY(-34px) scale(1); }
      100% { opacity:0;   transform:translateY(-56px) scale(0.88); }
    }
    @keyframes bk-score-tick {
      0%   { transform:scale(1); }
      40%  { transform:scale(1.22); }
      100% { transform:scale(1); }
    }
    @keyframes bk-dot-pop {
      0%   { transform:scale(1); }
      50%  { transform:scale(1.45); }
      100% { transform:scale(1); }
    }
    @keyframes bk-rank-toast {
      0%   { opacity:0; transform:translateX(-50%) scale(0.82) translateY(4px); }
      20%  { opacity:1; transform:translateX(-50%) scale(1.02) translateY(0); }
      70%  { opacity:1; transform:translateX(-50%) scale(1)    translateY(0); }
      100% { opacity:0; transform:translateX(-50%) scale(0.96) translateY(-4px); }
    }
    @keyframes bk-ring-expand {
      0%   { transform:scale(0.8); opacity:0.7; }
      100% { transform:scale(2.4); opacity:0; }
    }
    @keyframes bk-av-in-left  {
      from { opacity:0; transform:scale(0.5) translateX(-22px); }
      to   { opacity:1; transform:scale(1)   translateX(0); }
    }
    @keyframes bk-av-in-right {
      from { opacity:0; transform:scale(0.5) translateX(22px); }
      to   { opacity:1; transform:scale(1)   translateX(0); }
    }
    @keyframes bk-fade-up {
      from { opacity:0; transform:translateY(14px); }
      to   { opacity:1; transform:translateY(0); }
    }
    @keyframes bk-overlay-in {
      from { opacity:0; }
      to   { opacity:1; }
    }
    @keyframes bk-result-sweep {
      from { transform:translateX(-100%); }
      to   { transform:translateX(110%); }
    }
    @keyframes bk-score-line-in {
      from { opacity:0; transform:translateX(10px); }
      to   { opacity:1; transform:translateX(0); }
    }
  `;
  document.head.appendChild(s);
}

// ── Rank config ───────────────────────────────────────────────────────────────
export const RANK_CONFIG = [
  { name: 'Newcomer', min: 0,    max: 99   },
  { name: 'Regular',  min: 100,  max: 499  },
  { name: 'Player',   min: 500,  max: 999  },
  { name: 'Veteran',  min: 1000, max: 2499 },
  { name: 'Legend',   min: 2500, max: Infinity },
];

export function getRank(score) {
  const s   = score || 0;
  const idx = RANK_CONFIG.findIndex(r => s >= r.min && s <= r.max);
  const rank = RANK_CONFIG[idx] || RANK_CONFIG[RANK_CONFIG.length - 1];
  const next = RANK_CONFIG[idx + 1] || null;
  const pct  = next
    ? Math.min(100, Math.round(((s - rank.min) / (next.min - rank.min)) * 100))
    : 100;
  return { name: rank.name, next: next?.name || null, nextMin: next?.min || null, pct };
}

// =============================================================================
// FloatPop — single floating label ("+10", "+5 Speed", "+5 Streak")
// trigger: increment to fire; each increment = one animation
// =============================================================================
const POP_CFG = {
  base:   { label: '+10',       color: '#FBBC15', offsetX: 0,   fontSize: 18 },
  speed:  { label: '+5 Speed',  color: '#FE7210', offsetX: 28,  fontSize: 13 },
  streak: { label: '+5 Streak', color: '#0D9DA5', offsetX: -28, fontSize: 13 },
};

function FloatPop({ type = 'base', trigger = 0 }) {
  injectStyles();
  const [key,  setKey]  = useState(0);
  const [show, setShow] = useState(false);
  const prev = useRef(trigger);

  useEffect(() => {
    if (trigger !== prev.current && trigger > 0) {
      prev.current = trigger;
      setKey(k => k + 1);
      setShow(true);
      const t = setTimeout(() => setShow(false), 1200);
      return () => clearTimeout(t);
    }
  }, [trigger]);

  const cfg = POP_CFG[type] || POP_CFG.base;
  if (!show) return null;
  return (
    <span key={key} style={{
      position: 'absolute', top: 0,
      left: `calc(50% + ${cfg.offsetX}px)`,
      transform: 'translateX(-50%)',
      fontSize: cfg.fontSize, fontWeight: 800,
      color: cfg.color, pointerEvents: 'none',
      fontFamily: 'inherit', letterSpacing: '-0.3px',
      animation: 'bk-float-up 1.15s cubic-bezier(0.22,0.61,0.36,1) forwards',
      zIndex: 20, whiteSpace: 'nowrap',
    }}>
      {cfg.label}
    </span>
  );
}

// Renders all 3 pops in one relative container
// Usage: <ScorePops triggers={{ base, speed, streak }} />
// Increment the relevant counter to fire that pop.
export function ScorePops({ triggers = { base: 0, speed: 0, streak: 0 } }) {
  injectStyles();
  return (
    <div style={{ position: 'relative', height: 0, overflow: 'visible', pointerEvents: 'none' }}>
      <FloatPop type="base"   trigger={triggers.base} />
      <FloatPop type="speed"  trigger={triggers.speed} />
      <FloatPop type="streak" trigger={triggers.streak} />
    </div>
  );
}

// =============================================================================
// ScoreCounter — number that scale-pulses when value changes
// =============================================================================
export function ScoreCounter({ value, style = {} }) {
  injectStyles();
  const [key, setKey] = useState(0);
  const prev = useRef(value);
  useEffect(() => {
    if (value !== prev.current) { prev.current = value; setKey(k => k + 1); }
  }, [value]);
  return (
    <span key={key} style={{
      display: 'inline-block', fontWeight: 800,
      color: '#FBBC15', fontVariantNumeric: 'tabular-nums',
      animation: 'bk-score-tick 0.35s cubic-bezier(0.34,1.56,0.64,1) forwards',
      ...style,
    }}>
      {value}
    </span>
  );
}

// =============================================================================
// StreakBar — 5 dots, glows orange at 3+
// =============================================================================
export function StreakBar({ streak = 0 }) {
  injectStyles();
  const [prevStreak, setPrevStreak] = useState(streak);
  const [popIndex,   setPopIndex]   = useState(null);

  useEffect(() => {
    if (streak > prevStreak) {
      const idx = Math.min(streak, 5) - 1;
      setPopIndex(idx);
      const t = setTimeout(() => setPopIndex(null), 380);
      setPrevStreak(streak);
      return () => clearTimeout(t);
    }
    setPrevStreak(streak);
  }, [streak, prevStreak]);

  const isHot = streak >= 3;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      background: '#1A1A1A',
      border: `0.5px solid ${isHot ? 'rgba(254,114,16,0.45)' : '#2A2A2A'}`,
      borderRadius: 12, padding: '10px 14px',
      transition: 'border-color 0.3s, box-shadow 0.3s',
      boxShadow: isHot ? '0 0 18px rgba(254,114,16,0.10)' : 'none',
    }}>
      <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
        {Array.from({ length: 5 }, (_, i) => {
          const filled  = i < Math.min(streak, 5);
          const popping = i === popIndex;
          return (
            <div key={i} style={{
              width: 10, height: 10, borderRadius: '50%',
              background:  filled ? '#FE7210' : '#2A2A2A',
              border:      `1.5px solid ${filled ? '#FE7210' : '#555555'}`,
              boxShadow:   filled ? '0 0 8px rgba(254,114,16,0.45)' : 'none',
              transition:  'background 0.2s, border-color 0.2s, box-shadow 0.2s',
              animation:   popping ? 'bk-dot-pop 0.32s cubic-bezier(0.34,1.56,0.64,1) forwards' : 'none',
            }} />
          );
        })}
      </div>
      <span style={{
        fontSize: 13, fontWeight: isHot ? 700 : 500,
        color: isHot ? '#FE7210' : '#888888',
        marginLeft: 4, fontFamily: 'inherit', transition: 'color 0.3s',
      }}>
        {streak} in a row{isHot ? ' \u2014 on fire' : ''}
      </span>
      {isHot && (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
          stroke="#FE7210" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
          style={{ marginLeft: 2 }}>
          <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"/>
        </svg>
      )}
    </div>
  );
}

// =============================================================================
// ResultScoreCard — animated score breakdown for RESULT phase
// =============================================================================
function BoltIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
      stroke="#FE7210" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
    </svg>
  );
}
function FlameIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
      stroke="#FE7210" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"/>
    </svg>
  );
}

function SLine({ label, sublabel, value, color, bold = false, icon, delay = 0 }) {
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setVis(true), delay);
    return () => clearTimeout(t);
  }, [delay]);
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      opacity: vis ? 1 : 0,
      animation: vis ? 'bk-score-line-in 0.28s ease forwards' : 'none',
    }}>
      <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontFamily: 'inherit' }}>
        {icon && <span style={{ display: 'flex', alignItems: 'center' }}>{icon}</span>}
        <span style={{ fontSize: 13, color: '#888888', fontWeight: 500 }}>{label}</span>
        {sublabel && <span style={{ fontSize: 10, color: '#555555', fontWeight: 400 }}>{sublabel}</span>}
      </span>
      <span style={{
        fontSize: bold ? 15 : 13, fontWeight: 800,
        color, fontFamily: 'inherit', fontVariantNumeric: 'tabular-nums',
      }}>
        {value}
      </span>
    </div>
  );
}

export function ResultScoreCard({ isCorrect, basePts = 10, speedPts = 0, streakPts = 0, noAnswer = false }) {
  injectStyles();
  const total = basePts + speedPts + streakPts;
  return (
    <div style={{
      background: '#1A1A1A',
      border: `0.5px solid ${isCorrect ? 'rgba(13,157,165,0.38)' : '#2A2A2A'}`,
      borderRadius: 14, padding: '14px 16px', width: '100%',
      position: 'relative', overflow: 'hidden',
    }}>
      {isCorrect && (
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(90deg, transparent, rgba(13,157,165,0.07), transparent)',
          animation: 'bk-result-sweep 0.75s ease 0.08s forwards',
          pointerEvents: 'none',
        }} />
      )}
      <div style={{
        fontSize: 10, fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase',
        color: isCorrect ? '#0D9DA5' : noAnswer ? '#555555' : '#888888',
        marginBottom: 10, fontFamily: 'inherit',
      }}>
        {noAnswer ? 'No answer' : isCorrect ? 'Correct' : 'Incorrect'}
      </div>
      {isCorrect ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <SLine label="Base score"    value={`+${basePts}`}  color="#0D9DA5" delay={150} />
          {speedPts > 0 && (
            <SLine label="Speed bonus"  sublabel="answered in <5s" value={`+${speedPts}`}  color="#FE7210" icon={<BoltIcon />}  delay={310} />
          )}
          {streakPts > 0 && (
            <SLine label="Streak bonus" sublabel="3+ in a row"      value={`+${streakPts}`} color="#FBBC15" icon={<FlameIcon />} delay={470} />
          )}
          <div style={{ height: '0.5px', background: '#2A2A2A', margin: '2px 0' }} />
          <SLine label="This round" value={`+${total}`} color="#EFEFEF" bold delay={640} />
        </div>
      ) : (
        <div style={{
          fontSize: 13, color: '#888888', fontFamily: 'inherit',
          animation: 'bk-score-line-in 0.3s ease 0.12s forwards', opacity: 0,
        }}>
          {noAnswer ? "You didn\'t submit in time." : 'Better luck next round.'}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// XPBar — rank badge + animated progress bar (for Profile)
// =============================================================================
export function XPBar({ score = 0, animateOnMount = true }) {
  injectStyles();
  const rank    = getRank(score);
  const fillRef = useRef(null);
  const mounted = useRef(false);

  useEffect(() => {
    if (!fillRef.current) return;
    const target = rank.pct + '%';
    if (!mounted.current && animateOnMount) {
      fillRef.current.style.width      = '0%';
      fillRef.current.style.transition = 'none';
      requestAnimationFrame(() => requestAnimationFrame(() => {
        if (!fillRef.current) return;
        fillRef.current.style.transition = 'width 1.3s cubic-bezier(0.22,0.61,0.36,1)';
        fillRef.current.style.width      = target;
      }));
    } else {
      fillRef.current.style.transition = 'width 0.9s cubic-bezier(0.22,0.61,0.36,1)';
      fillRef.current.style.width      = target;
    }
    mounted.current = true;
  }, [score, rank.pct, animateOnMount]);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          background: 'rgba(251,188,21,0.10)',
          border: '0.5px solid rgba(251,188,21,0.28)',
          borderRadius: 99, padding: '4px 10px',
        }}>
          <div style={{
            width: 6, height: 6, borderRadius: '50%',
            background: '#FBBC15', boxShadow: '0 0 5px rgba(251,188,21,0.5)',
          }} />
          <span style={{ fontSize: 11, fontWeight: 700, color: '#FBBC15', letterSpacing: '0.5px', fontFamily: 'inherit' }}>
            {rank.name.toUpperCase()}
          </span>
        </div>
        <span style={{ fontSize: 11, color: '#555555', fontWeight: 500, fontFamily: 'inherit' }}>
          {rank.next ? `${score} / ${rank.nextMin} to ${rank.next}` : `${score} pts \u00b7 Max rank`}
        </span>
      </div>
      <div style={{ width: '100%', height: 4, background: '#2A2A2A', borderRadius: 99, overflow: 'hidden', marginBottom: 5 }}>
        <div ref={fillRef} style={{
          height: '100%', width: '0%',
          background: '#FBBC15', borderRadius: 99,
          boxShadow: '0 0 7px rgba(251,188,21,0.38)',
        }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 10, color: '#555555', fontWeight: 500, fontFamily: 'inherit' }}>{rank.name}</span>
        <span style={{ fontSize: 10, color: '#888888', fontWeight: 500, fontFamily: 'inherit' }}>{rank.next || 'Legend'}</span>
      </div>
    </div>
  );
}

// =============================================================================
// RankUpToast — yellow pill, fires for 2.4s
// =============================================================================
export function RankUpToast({ show = false, newRank = '' }) {
  injectStyles();
  const [key,     setKey]     = useState(0);
  const [visible, setVisible] = useState(false);
  const prev = useRef(show);

  useEffect(() => {
    if (show && !prev.current) {
      setKey(k => k + 1);
      setVisible(true);
      const t = setTimeout(() => setVisible(false), 2500);
      prev.current = show;
      return () => clearTimeout(t);
    }
    prev.current = show;
  }, [show]);

  if (!visible) return null;
  return (
    <div key={key} style={{
      position: 'fixed', top: 64, left: '50%',
      background: '#FBBC15', color: '#5A3A00',
      fontSize: 13, fontWeight: 800,
      padding: '10px 22px', borderRadius: 99,
      pointerEvents: 'none', whiteSpace: 'nowrap',
      zIndex: 9999, letterSpacing: '0.3px', fontFamily: 'inherit',
      animation: 'bk-rank-toast 2.4s ease forwards',
    }}>
      Rank up \u2014 You\'re a {newRank}
    </div>
  );
}

// =============================================================================
// MatchOverlay — full-screen "It\'s a match"
// =============================================================================
function burstParticles(container) {
  if (!container) return;
  container.innerHTML = '';
  const colors = ['#FBBC15', '#0D9DA5', '#FE7210', '#EFEFEF'];
  const cx = window.innerWidth / 2;
  const cy = window.innerHeight / 2;
  for (let i = 0; i < 26; i++) {
    const p     = document.createElement('div');
    const angle = (i / 26) * Math.PI * 2;
    const dist  = 55 + Math.random() * 120;
    const tx    = Math.cos(angle) * dist;
    const ty    = Math.sin(angle) * dist;
    const size  = 3 + Math.random() * 4;
    p.style.cssText = `position:absolute;left:${cx}px;top:${cy}px;width:${size}px;height:${size}px;background:${colors[i % colors.length]};border-radius:${Math.random() > 0.5 ? '50%' : '2px'};pointer-events:none;`;
    container.appendChild(p);
    p.animate([
      { opacity: 0, transform: 'translate(-50%,-50%) scale(0)' },
      { opacity: 1, transform: `translate(calc(-50% + ${tx * 0.3}px),calc(-50% + ${ty * 0.3}px)) scale(1)`, offset: 0.18 },
      { opacity: 0.9, transform: `translate(calc(-50% + ${tx}px),calc(-50% + ${ty}px)) scale(0.75)`, offset: 0.7 },
      { opacity: 0, transform: `translate(calc(-50% + ${tx * 1.25}px),calc(-50% + ${ty * 1.25 + 36}px)) scale(0.15)` },
    ], { duration: 850 + Math.random() * 420, delay: Math.random() * 180, easing: 'cubic-bezier(0.22,0.61,0.36,1)', fill: 'forwards' });
  }
}

export function MatchOverlay({ visible, myAlias = '', theirAlias = '', onChat, onDismiss }) {
  injectStyles();
  const [active, setActive] = useState(false);
  const [phase,  setPhase]  = useState(0);
  const particleRef = useRef(null);
  const timers      = useRef([]);

  const clear = () => timers.current.forEach(clearTimeout);

  const fire = useCallback(() => {
    clear();
    setActive(true); setPhase(1);
    timers.current = [
      setTimeout(() => setPhase(2), 750),
      setTimeout(() => setPhase(3), 700),
      setTimeout(() => setPhase(4), 1000),
      setTimeout(() => burstParticles(particleRef.current), 220),
    ];
  }, []);

  const reset = useCallback(() => {
    clear(); setActive(false); setPhase(0);
    if (particleRef.current) particleRef.current.innerHTML = '';
  }, []);

  useEffect(() => {
    if (visible) fire(); else reset();
    return clear;
  }, [visible, fire, reset]);

  if (!active) return null;

  const myL    = (myAlias    || '?').charAt(0).toUpperCase();
  const theirL = (theirAlias || '?').charAt(0).toUpperCase();

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(13,13,13,0.97)',
      zIndex: 1400, display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      animation: 'bk-overlay-in 0.28s ease forwards', padding: '0 24px',
    }}>
      <div ref={particleRef} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }} />
      {[0, 0.5, 1].map((delay, i) => (
        <div key={i} style={{
          position: 'absolute', width: 160, height: 160, borderRadius: '50%',
          border: '1.5px solid rgba(13,157,165,0.25)',
          animation: `bk-ring-expand 2.1s ease-in-out ${delay}s infinite`,
          opacity: 1 - i * 0.25, pointerEvents: 'none',
        }} />
      ))}

      {/* Headline */}
      <div style={{
        fontSize: 10, fontWeight: 700, letterSpacing: '3.5px', textTransform: 'uppercase',
        color: '#888888', marginBottom: 22, fontFamily: 'inherit',
        opacity: phase >= 3 ? 1 : 0,
        transform: phase >= 3 ? 'translateY(0)' : 'translateY(10px)',
        transition: 'opacity 0.45s ease, transform 0.45s ease',
      }}>
        It's a match
      </div>

      {/* Avatars */}
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', marginBottom: 26 }}>
        <div style={{
          position: 'absolute', height: 1.5,
          background: 'linear-gradient(90deg,#0D9DA5,#FBBC15)',
          top: '50%', left: 0, right: 0, transform: 'translateY(-50%)',
          width: phase >= 2 ? '100%' : '0%', transition: 'width 0.5s ease',
          opacity: 0.5, borderRadius: 99,
        }} />
        <div style={{
          width: 70, height: 70, borderRadius: '50%',
          background: '#0D9DA5', color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 26, fontWeight: 800, fontFamily: 'inherit',
          border: '2.5px solid rgba(13,13,13,0.97)', position: 'relative', zIndex: 2,
          animation: phase >= 1 ? 'bk-av-in-left 0.55s cubic-bezier(0.34,1.56,0.64,1) 0.15s both' : 'none',
        }}>{myL}</div>
        <div style={{
          width: 70, height: 70, borderRadius: '50%',
          background: '#FBBC15', color: '#5A3A00',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 26, fontWeight: 800, fontFamily: 'inherit',
          border: '2.5px solid rgba(13,13,13,0.97)',
          marginLeft: -12, position: 'relative', zIndex: 1,
          animation: phase >= 1 ? 'bk-av-in-right 0.55s cubic-bezier(0.34,1.56,0.64,1) 0.3s both' : 'none',
        }}>{theirL}</div>
      </div>

      {/* Names */}
      <div style={{
        fontSize: 26, fontWeight: 900, textAlign: 'center',
        letterSpacing: '-0.8px', lineHeight: 1.15, fontFamily: 'inherit',
        opacity: phase >= 3 ? 1 : 0,
        transform: phase >= 3 ? 'translateY(0)' : 'translateY(12px)',
        transition: 'opacity 0.45s ease 0.1s, transform 0.45s ease 0.1s',
      }}>
        <span style={{ color: '#0D9DA5' }}>{myAlias || 'You'}</span>
        <span style={{ color: '#888888', fontWeight: 500, fontSize: 20 }}> &amp; </span>
        <span style={{ color: '#FBBC15' }}>{theirAlias || 'Them'}</span>
      </div>

      {/* Sub */}
      <div style={{
        fontSize: 13, color: '#888888', textAlign: 'center',
        marginTop: 10, lineHeight: 1.55, fontFamily: 'inherit', maxWidth: 280,
        opacity: phase >= 3 ? 1 : 0,
        transform: phase >= 3 ? 'translateY(0)' : 'translateY(12px)',
        transition: 'opacity 0.45s ease 0.2s, transform 0.45s ease 0.2s',
      }}>
        You both waved at each other.<br/>Say hi before the night's over.
      </div>

      {/* Actions */}
      <div style={{
        display: 'flex', flexDirection: 'column', gap: 10,
        width: '100%', maxWidth: 300, marginTop: 32,
        opacity: phase >= 4 ? 1 : 0,
        transform: phase >= 4 ? 'translateY(0)' : 'translateY(14px)',
        transition: 'opacity 0.45s ease, transform 0.45s ease',
      }}>
        <button onClick={onChat} style={{
          width: '100%', height: 48, background: '#FBBC15', color: '#5A3A00',
          fontSize: 15, fontWeight: 800, border: 'none', borderRadius: 12,
          cursor: 'pointer', fontFamily: 'inherit', letterSpacing: '0.2px',
        }}>
          Start chatting
        </button>
        <button onClick={onDismiss} style={{
          width: '100%', height: 40, background: 'transparent', color: '#888888',
          fontSize: 13, fontWeight: 600, border: '0.5px solid #2A2A2A',
          borderRadius: 12, cursor: 'pointer', fontFamily: 'inherit',
        }}>
          Maybe later
        </button>
      </div>
    </div>
  );
}
